import React, { useState, useEffect, useCallback } from 'react';
import {
    UserPlus,
    Search,
    MoreVertical,
    MessageSquare,
    Phone,
    Mail,
    Trash2,
    Edit2,
    User,
    Plus,
    ArrowLeft,
    X,
    Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
    getContacts,
    createContact,
    updateContact,
    deleteContact,
    createConversation,
    getConversations
} from '../lib/api';
import { cn, getInitials } from '../lib/utils';
import { toast } from 'sonner';

export const Contacts = ({ currentUser, isMobile, onNavigateToConversation }) => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        plan: '',
        contract: ''
    });
    const [saving, setSaving] = useState(false);

    const fetchContacts = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getContacts();
            setContacts(res.data);
        } catch (error) {
            console.error('Error fetching contacts:', error);
            toast.error('Erro ao carregar contatos');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchContacts();
    }, [fetchContacts]);

    const filteredContacts = contacts.filter(contact => {
        const search = searchTerm.toLowerCase();
        return (
            contact.name?.toLowerCase().includes(search) ||
            contact.phone.includes(search) ||
            contact.email?.toLowerCase().includes(search)
        );
    });

    const handleOpenAdd = () => {
        setEditingContact(null);
        setFormData({
            name: '',
            phone: '',
            email: '',
            plan: '',
            contract: ''
        });
        setShowAddDialog(true);
    };

    const handleOpenEdit = (contact) => {
        setEditingContact(contact);
        setFormData({
            name: contact.name || '',
            phone: contact.phone || '',
            email: contact.email || '',
            plan: contact.plan || '',
            contract: contact.contract || ''
        });
        setShowAddDialog(true);
    };

    const handleSaveContact = async () => {
        if (!formData.phone) {
            toast.error('O telefone é obrigatório');
            return;
        }

        setSaving(true);
        try {
            if (editingContact) {
                await updateContact(editingContact.id, formData);
                toast.success('Contato atualizado com sucesso');
            } else {
                await createContact(formData);
                toast.success('Contato criado com sucesso');
            }
            setShowAddDialog(false);
            fetchContacts();
        } catch (error) {
            console.error('Error saving contact:', error);
            toast.error(error.response?.data?.detail || 'Erro ao salvar contato');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteContact = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir este contato?')) {
            try {
                await deleteContact(id);
                toast.success('Contato excluído');
                fetchContacts();
            } catch (error) {
                console.error('Error deleting contact:', error);
                toast.error('Erro ao excluir contato');
            }
        }
    };

    const handleStartConversation = async (contact) => {
        try {
            const res = await createConversation({
                customer_phone: contact.phone.replace(/\D/g, ''),
                customer_name: contact.name || contact.phone,
                instance_id: 'default'
            });

            if (onNavigateToConversation) {
                onNavigateToConversation(res.data.id);
            } else {
                toast.info('Vá para a aba de Conversas para ver o chat.');
            }
        } catch (error) {
            console.error('Error starting conversation:', error);
            toast.error('Erro ao iniciar conversa');
        }
    };

    return (
        <div className="flex-1 flex flex-col overflow-hidden bg-slate-50">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <UserPlus className="text-primary w-5 h-5" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-slate-900">Contatos</h1>
                        <p className="text-sm text-slate-500">{contacts.length} contatos cadastrados</p>
                    </div>
                </div>
                <Button onClick={handleOpenAdd} className="gap-2">
                    <Plus size={18} />
                    <span className="hidden sm:inline">Novo Contato</span>
                </Button>
            </div>

            {/* Toolbar */}
            <div className="px-6 py-4 bg-white border-b border-slate-200 flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <Input
                        placeholder="Buscar por nome, telefone ou email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 bg-slate-50 border-slate-200"
                    />
                </div>
            </div>

            {/* Content */}
            <ScrollArea className="flex-1">
                <div className="p-6">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <Loader2 className="w-10 h-10 text-primary animate-spin mb-4" />
                            <p className="text-slate-500">Carregando contatos...</p>
                        </div>
                    ) : filteredContacts.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-dashed border-slate-300">
                            <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center mb-4">
                                <Search className="w-8 h-8 text-slate-300" />
                            </div>
                            <h3 className="text-lg font-medium text-slate-900">Nenhum contato encontrado</h3>
                            <p className="text-slate-500 max-w-xs text-center mt-1">
                                Tente ajustar sua busca ou adicione um novo contato.
                            </p>
                            <Button variant="outline" onClick={handleOpenAdd} className="mt-6">
                                Adicionar primeiro contato
                            </Button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {filteredContacts.map((contact) => (
                                <div
                                    key={contact.id}
                                    className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow group"
                                >
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <Avatar className="w-12 h-12 border-2 border-slate-100">
                                                <AvatarFallback className="bg-primary/5 text-primary text-lg font-semibold">
                                                    {getInitials(contact.name || contact.phone)}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className="min-w-0">
                                                <h4 className="font-semibold text-slate-900 truncate">
                                                    {contact.name || 'Sem nome'}
                                                </h4>
                                                <div className="flex items-center gap-1 text-slate-500">
                                                    <Phone size={12} />
                                                    <span className="text-xs">{contact.phone}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <MoreVertical size={18} />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => handleOpenEdit(contact)}>
                                                    <Edit2 className="mr-2" size={14} />
                                                    Editar
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    className="text-destructive"
                                                    onClick={() => handleDeleteContact(contact.id)}
                                                >
                                                    <Trash2 className="mr-2" size={14} />
                                                    Excluir
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>

                                    {contact.email && (
                                        <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                                            <Mail size={14} className="text-slate-400" />
                                            <span className="truncate">{contact.email}</span>
                                        </div>
                                    )}

                                    {(contact.plan || contact.contract) && (
                                        <div className="flex flex-wrap gap-2 mb-4">
                                            {contact.plan && (
                                                <Badge variant="secondary" className="text-[10px] font-normal px-2 py-0">
                                                    {contact.plan}
                                                </Badge>
                                            )}
                                            {contact.contract && (
                                                <Badge variant="outline" className="text-[10px] font-normal px-2 py-0">
                                                    Contrato: {contact.contract}
                                                </Badge>
                                            )}
                                        </div>
                                    )}

                                    <div className="pt-4 border-t border-slate-100 flex gap-2">
                                        <Button
                                            variant="ghost"
                                            className="flex-1 text-xs gap-2"
                                            onClick={() => handleStartConversation(contact)}
                                        >
                                            <MessageSquare size={14} />
                                            Mensagem
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </ScrollArea>

            {/* Add/Edit Dialog */}
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>{editingContact ? 'Editar Contato' : 'Novo Contato'}</DialogTitle>
                        <DialogDescription>
                            Preencha as informações do contato abaixo.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">Nome</label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Ex: João Silva"
                            />
                        </div>
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">Telefone *</label>
                            <Input
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                placeholder="Ex: 5511999999999"
                            />
                        </div>
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">Email</label>
                            <Input
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="Ex: joao@exemplo.com"
                                type="email"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="grid gap-2">
                                <label className="text-sm font-medium">Plano</label>
                                <Input
                                    value={formData.plan}
                                    onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                                    placeholder="Ex: Premium"
                                />
                            </div>
                            <div className="grid gap-2">
                                <label className="text-sm font-medium">Contrato</label>
                                <Input
                                    value={formData.contract}
                                    onChange={(e) => setFormData({ ...formData, contract: e.target.value })}
                                    placeholder="Ex: 12345"
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={handleSaveContact} disabled={saving}>
                            {saving && <Loader2 className="mr-2 animate-spin w-4 h-4" />}
                            {editingContact ? 'Salvar Alterações' : 'Criar Contato'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
